// @flow
import * as React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import PubNubReact from 'pubnub-react';
import type { TPlayerWithFixture } from '../../modules/types/player';
import type { TRootStore } from '../../modules/types';

import * as actions from "../../modules/actions";
import * as selectors from "../../modules/selectors";

import {
	ButtonLoadMore,
	DraftFilters,
	PlayerProfilePopup,
	PlayerInfo,
	SwitchButton,
	CoachLockIcon,
	DragIcon,
	CheckboxControlled as Checkbox,
	ModalContainer,
	ModalInnerTitle,
} from '../../components';
import ArrowUp from '../../components/Icons/ArrowUp';
import ArrowDown from '../../components/Icons/ArrowDown';
import Table, { TBody, Tr, Td } from '../../components/TableX';
import { PageTitle, ThStat } from '../../components';
import { DraftTableHeader } from "../../components";
import DraftOrderWidget from "../../components/DraftOrderWidget";
import TeamViewWidget from "../../components/TeamViewWidget";
import DraftLayout from '../../components/Draft/DraftLayout';
import Aside from '../../components/Draft/Aside';
import THead from '../../components/Draft/THead';
import TrRow from '../../components/Draft/TrRow';
import TdYourTeam from '../../components/Draft/TdYourTeam';
import TdStat from '../../components/Draft/TdStat';
import ThIgnore from '../../components/Draft/ThIgnore';
import Expander from '../../components/Draft/Expander';
import DraftTitleContainer from '../../components/Draft/DraftTitleContainer';
import DraftOrder from '../../components/Draft/DraftOrder';
import ThToolTip from '../../components/ThToolTip';
import NoDrop from '../../components/Icons/NoDrop';
import { withDraftCore } from "../../components/Draft/withDraftCore";
import type { TDraftPassedProps } from "../../components/Draft/withDraftCore";
import { isMobile } from '../../utils';
import { getSquadSize } from '../../helpers/league';
import DraftTeamListOrderIndicator from './liveDraft/draftTeamListOrderIndicator';
import DraftBarContainer from './liveDraft/draftBarContainer';
import AutoPickToggle from './liveDraft/autoPickToggle';
import ThStatIgnored from './preDraft/thStatIgnored';
import MakePickPopup from "./liveDraft/makePickPopup";
import DraftButton from "./liveDraft/draftButton";
import DraftTurnTimer from "./liveDraft/draftTurnTimer";

const TrRowDrafted = styled(Tr)`
	background: #F6F6F6 !important;
	border-bottom-color: #F6F6F6;
`;
const DragIconWrapper = styled.div`
	visibility: hidden;
`;
const TdDraftedPlayerInfo = styled(TdStat)`
	vertical-align: middle;
`;
const DraftedPlayerPickNum = styled.p`
	font-weight: bold;
	font-size: 12px;
	color: #003A31;
	line-height: 1.5;
`;
const DraftedPlayerPickUserName = styled.p`
	font-size: 12px;
	color: #777777;
	line-height: 1.5;
`;
const DraftedPlayersList = styled.div`
	height: 122px;
	overflow-y: scroll;
	border-bottom: 5px solid #E5EBEA;
	${THead} {
		tr {
			border: none;
		}

		th {
			padding: 0;
			height: 0;
			overflow:hidden;
			border: none;

			& > * {
				overflow: hidden;
				height: 0 !important;
				position: absolute;
				top: -9999999px !important;
			}
		}
	}
`;
const TableFixed = styled(Table)`
	width: 100%;
	table-layout: fixed;
`;

const TableDraftedFixed = styled(TableFixed)`
	tbody {
		td {
			height: 61px;
			color: rgba(11,27,23,0.29);
		}
	}
`;

const NoDropWrapper = styled.div`
	width: 50px;
	height: 50px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 2px auto;
`;

type Props = TDraftPassedProps & {
	showDraftOrder: typeof actions.leagueDraft.showDraftOrder,
	postAutoPickFlag: typeof actions.teamsDraft.postAutoPickFlag,
	postPickedPlayer: typeof actions.teamsDraft.postPickedPlayer,
	teams: Object,
	teams_by_id: Object,
	users_by_id: Object,
	team_error: string,
	clearTeamErrors: typeof actions.teamsDraft.clearTeamErrors,
}

type State = {
	draft_order_widget_open: boolean,
	team_view_widget_open: boolean,
	draft_guide_widget_open: boolean,
	player_for_pick?: TPlayerWithFixture | typeof undefined,
	player_drafted_popup: Object,
	picks: Array<Object>,
	drafted_players: Array<number>,
	live_draft_complete: boolean,
	is_my_turn: boolean,
	is_auto_pick_enabled: boolean | typeof undefined,
	next_pick_at: string,
	last_pick?: Object | typeof undefined,
	picks_by_player_id?: { [key: string]: TPlayerWithFixture },
	picked_players_by_pick?: { [key: string]: TPlayerWithFixture },
}

class LiveDraftContainer extends React.Component<Props, State> {
	state = {
		picks: [],
		drafted_players: [],
		draft_order_widget_open: false,
		team_view_widget_open: true,
		draft_guide_widget_open: false,
		live_draft_complete: false,
		is_my_turn: false,
		player_for_pick: undefined,
		player_drafted_popup: undefined,
		is_auto_pick_enabled: undefined,
		last_pick: undefined,
		next_pick_at: (new Date()).toString(),
		picks_by_player_id: {},
		picked_players_by_pick: {}
	};

	pubnub = new PubNubReact({
		publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY,
		subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY,
		ssl: true,
	});

	onToggleDraftOrderWidget() {
		const draft_order_widget_open = !this.state.draft_order_widget_open;
		this.setState({ draft_order_widget_open });
	}

	onToggleTeamViewWidget() {
		const team_view_widget_open = !this.state.team_view_widget_open;
		this.setState({ team_view_widget_open });
	}

	onToggleDraftGuideWidget() {
		const draft_guide_widget_open = !this.state.draft_guide_widget_open;
		this.setState({ draft_guide_widget_open });
	}

	constructor(props, state) {
		super(props, state);

		this.pubnub.init(this);

		_.bindAll(this, [
			'onToggleDraftOrderWidget',
			'onToggleDraftGuideWidget',
			'onToggleTeamViewWidget',
			'setPlayerPopup',
			'onClickIgnore',
			'pubNubMessagesHandler',
			'pubNubMessageHandler',
			'isDrafted',
			'confirmPlayerPick',
			'closePickModal',
			'closePlayerPopup',
			'onToggleAutoPick',
		]);
	}

	subscribeToPubNub() {
		const { pub_nub_channel } = this.props;

		this.pubnub.subscribe({
			channels: [pub_nub_channel]
		});

		this.pubnub.getMessage(pub_nub_channel, this.pubNubMessageHandler);

		return this;
	}

	getMessages(args: Object, callback) {
		this.pubnub.history({
			// search starting from this timetoken
			start: args.startToken,
			channel: args.channel,
			// false - search forwards through the timeline
			// true - search backwards through the timeline
			reverse: args.reverse,
			// limit number of messages per request to this value; default/max=100
			count: args.pagesize,
			// include each returned message's publish timetoken
			includeTimetoken: false,
			// prevents JS from truncating 17 digit timetokens
			stringifiedTimeToken: true
		},
		(status, response) => {
			// holds the accumulation of resulting messages across all iterations
			let results = args.results;

			// the retrieved messages from history for this iteration only
			const msgs = response.messages,
				// timetoken of the first message in response
				firstTT = response.startTimseToken,
				// timetoken of the last message in response
				lastTT = response.endTimeToken;

			// if no max results specified, default to 1000
			args.max = _.get(args, 'max', 1000);

			if (!_.isEmpty(msgs)) {
				// first iteration, results is undefined, so initialize with first history results
				if (!results) {
					results = msgs;
				}
				// subsequent iterations, results has previous iterartions' results, so concat
				// but concat to end of results if reverse true,
				// otherwise prepend to begining of results
				else {
					args.reverse ? results = results.concat(msgs) : results = msgs.concat(results);
				}
			}

			// we keep asking for more messages if # messages returned by last request is the
			// same at the pagesize AND we still have reached the total number of messages requested
			// same as the opposit of !(msgs.length < pagesize || total == max)
			if (msgs.length === args.pagesize && results.length < args.max) {
				this.getMessages({
					channel: args.channel,
					max: args.max,
					reverse: args.reverse,
					pagesize: args.pagesize,
					startToken: args.reverse ? lastTT : firstTT,
					results: results
				}, callback);
			}
			// we've reached the end of possible messages to retrieve or hit the 'max' we asked for
			// so invoke the callback to the original caller of getMessages providing
			// the total message results
			else {
				callback(results);
			}
		});
	};

	get is_auto_pick_enabled() {
		const { league, teams_by_id } = this.props;
		const { is_auto_pick_enabled } = this.state;

		if (_.isUndefined(is_auto_pick_enabled)) {
			return _.eq(_.get(teams_by_id, `${league.team_id}.missed_turn`, 0), 2);
		}

		return is_auto_pick_enabled;
	}

	getNextPickDate(last_pick) {
		const
			{ league, server_time_diff } = this.props,
			now = Date.now(),
			be_turn_time = (new Date(_.get(last_pick, 'turn_at', now))).valueOf(),
			draft_turn = _.get(league, 'draft_turn');

		if (!draft_turn) {
			return new Date(be_turn_time + server_time_diff);
		}

		const
			league_turn_time = draft_turn * 1000,
			sent_at = (new Date(_.get(last_pick, 'sent_at', now))).valueOf(),
			turn_time = sent_at + league_turn_time + server_time_diff;

		return new Date(turn_time);
	}

	pubNubMessagesHandler(result) {
		if (!result) {
			return;
		}

		const picks = result.map(msg => msg.entry || msg);
		const pick = _.last(picks);

		if (_.eq(_.get(pick, 'draft_status'), 'complete')) {
			return this.setState({ live_draft_complete: true });
		}

		const drafted_players = picks.map(
			_.property('picked_player_id')
		).filter(_.identity);

		const { user } = this.props;

		const picks_with_players = picks.filter(
			_.property('picked_player_id')
		);
		const picked_players_by_pick = _.keyBy(picks_with_players, 'pick');

		this.setState({
			picks,
			drafted_players,
			picks_by_player_id: _.keyBy(picks_with_players, 'picked_player_id'),
			picked_players_by_pick,
			is_my_turn: _.eq(_.get(pick, 'turn_user_id'), user.id),
			next_pick_at: this.getNextPickDate(pick).toString(),
			last_pick: pick,
		});
	}

	/**
	 * @todo This code can be more optimized, if we will not call showDraftOrder method
	 * after every message, but will update teams manually. This will decrease BE load.
	 */
	pubNubMessageHandler(result) {
		if (!result) {
			return;
		}

		const
			{ message } = result,
			picks = this.state.picks.slice();

		picks.push(message);

		this.updatePreDraftList(message).pubNubMessagesHandler(picks);

		const {
			league,
			showDraftOrder,
			match: { params: { league_id } }
		} = this.props;

		showDraftOrder({ league_id, details: 1 });

		this.setState({
			is_auto_pick_enabled: Boolean(_.get(message, `autopicks.${league.team_id}`, 0)),
		});
	}

	updatePreDraftList(message) {
		const {
				league: { team_id },
				pre_draft_list,
				removePlayerFromPreDraftList,
			} = this.props,
			player_id = _.get(message, 'picked_player_id');

		if (player_id && pre_draft_list.includes(player_id)) {
			removePlayerFromPreDraftList({
				player_id,
				team_id,
				silent: true,
			});
		}

		return this;
	}

	componentDidMount() {
		this.subscribeToPubNub().getMessages({
			channel: this.props.pub_nub_channel,
			pagesize: 100,
			reverse: true
		}, this.pubNubMessagesHandler);

		const {
			showDraftOrder,
			match: { params: { league_id } },
		} = this.props;

		showDraftOrder({ league_id, details: 1 });
	}

	componentDidUpdate({ league, server_time_diff }, { last_pick }) {
		const
			is_league_received = (
				_.get(league, 'draft_turn') !== _.get(this.props.league, 'draft_turn')
			),
			time_diff_changed = server_time_diff !== this.props.server_time_diff,
			last_pick_changed = last_pick !== this.state.last_pick;

		if (is_league_received || time_diff_changed || last_pick_changed) {
			const next_pick_at = this.getNextPickDate(this.state.last_pick).toString();
			this.setState({ next_pick_at });
		}
	}

	componentWillUnmount() {
		this.pubnub.unsubscribe({
			channels: [this.props.pub_nub_channel]
		});
	}

	get players_filter() {
		const { players_filter } = this.props;

		return _.overEvery([
			_.negate(this.isDrafted),
			players_filter
		]);
	}

	get players() {
		const { players, orderByComparator, has_filters } = this.props;
		const players_copy = players.slice();

		if (has_filters) {
			return players_copy.filter(this.players_filter).sort(orderByComparator);
		}

		const { players_show_limit } = this.props;

		return players_copy.filter(_.negate(this.isDrafted))
			.sort(orderByComparator)
			.slice(0, players_show_limit);
	}

	get is_sidebar_open() {
		const {
			draft_order_widget_open,
			draft_guide_widget_open,
			team_view_widget_open,
		} = this.state;

		return draft_order_widget_open || draft_guide_widget_open || team_view_widget_open;
	}

	get drafted_players() {
		const { players } = this.props;
		const { picks_by_player_id } = this.state;

		return _.sortBy(
			players.slice().filter(this.isDrafted),
			player => _.get(picks_by_player_id, `${player.id}.pick`, 0)
		);
	}

	get load_more_btn() {
		if (this.props.load_more_is_hidden) {
			return null;
		}

		return <ButtonLoadMore onClick={this.props.onLoadMoreClick}>
			Load More <ArrowDown />
		</ButtonLoadMore>;
	}

	// eslint-disable-next-line complexity
	get table_header() {
		const { order_by_desc, onSortColumnChange, isActiveTab, table_keys } = this.props;

		const cells = table_keys.map(({ key, prop, tooltip }) => (
			<ThStat
				className={`${isActiveTab(prop)} player-stats`}
				onClick={() => onSortColumnChange(prop)} key={key}>
				<div>
					<div className="highlighter" />
					<span className="column-name">{key}</span>
					{isActiveTab(prop) && !order_by_desc ?
						<ArrowUp color='#ffffff' className='top' /> : null}
					{isActiveTab(prop) && order_by_desc ?
						<ArrowDown color='#ffffff' className='down' /> : null}
					{tooltip && <ThToolTip text={tooltip} />}
				</div>
			</ThStat>
		));

		const
			{ is_coach_active } = this.props,
			draft_order_key = 'draft_order',
			{ is_my_turn } = this.state;

		return (
			<THead className={is_coach_active ? 'coach-stats' : 'basic-stats'}>
				<Tr>
					<ThIgnore width="50px">
						<span>
							Drag &
							<br />
							Drop
						</span>
					</ThIgnore>
					<ThStat
						className={`${isActiveTab(draft_order_key)}`}
						onClick={() => onSortColumnChange(draft_order_key)}
						width="132px"
					>
						<div>
							<div className="highlighter" />
							<span className="column-name">Draft position</span>
							{isActiveTab(draft_order_key) && !order_by_desc ?
								<ArrowUp color='#ffffff' className='top' /> : null}
							{isActiveTab(draft_order_key) && order_by_desc ?
								<ArrowDown color='#ffffff' className='down' /> : null}
						</div>
					</ThStat>

					<ThStatIgnored width={isMobile() ? '25%' : '240px'}>
						<span />
					</ThStatIgnored>
					{cells}
					<ThIgnore>
						<span>{is_my_turn ? 'Draft' : 'Ignore'}</span>
					</ThIgnore>
				</Tr>
			</THead>
		);
	}

	getTableMessage(message) {
		const size_of_predefined_columns = 3,
			total_columns_size = _.size(this.props.table_keys) + size_of_predefined_columns;

		return (
			<TBody>
				<Tr>
					<Td align="center" colSpan={total_columns_size}>
						<Expander>{message}</Expander>
					</Td>
				</Tr>
			</TBody>
		);
	}
	get columns_size() {
		const size_of_predefined_columns = 4;
		return _.size(this.props.table_keys) + size_of_predefined_columns;
	}

	isDrafted(player) {
		return this.state.drafted_players.includes(player.id);
	}

	get table_body() {
		const { is_pending_players, pre_draft_list_size, isActiveTab } = this.props;


		if (is_pending_players) {
			return this.getTableMessage('Loading, please wait...');
		}

		const players_list = this.players;

		const { is_my_turn } = this.state;

		const {
			onChangePlayerOrder,
			updatePlayerPreDraftListOrder,
			has_assistant_coach,
			is_coach_active,
			is_dnd_allowed,
			onDrop,
			onDragEnd,
			onDragLeave,
			onDragOver,
			onDragEnter,
			onDragStart,
			onEnableDrag,
			onDisableDrag,
			filters: { by_drafted }
		} = this.props;

		const cells = players_list.map(player => {
			const { draft_order } = player,
				is_ignored = !draft_order,
				is_drafted = this.isDrafted(player),
				is_not_able_to_change_order = _.some(
					[is_ignored, is_drafted],
					_.identity,
				),
				is_dnd_enabled = !is_not_able_to_change_order && is_dnd_allowed,
				is_draft_disabled = _.some([
					!is_my_turn,
					is_drafted,
					_.isEmpty(this.getAvailablePositions(player)),
				], _.identity);

			return (
				<TrRow
					draggable="false"
					data-drag-and-drop-enabled={is_dnd_enabled}
					data-player-id={player.id}
					onDrop={onDrop}
					onDragEnd={onDragEnd}
					onDragEnter={onDragEnter}
					onDragLeave={onDragLeave}
					onDragOver={onDragOver}
					onDragStart={onDragStart}
					key={player.id}
				>
					<Td>
						<DragIcon
							onMouseEnter={onEnableDrag}
							onMouseLeave={onDisableDrag}
							disabled={!is_dnd_enabled}
						/>
					</Td>
					<TdYourTeam alignText="right">
						<div>
							<DraftOrder
								disabled={is_not_able_to_change_order}
								onChange={onChangePlayerOrder}
								name="draft_order"
								data-id={player.id}
								value={draft_order || '-'}
							/>
							<div>
								<SwitchButton
									up={true}
									disabled={is_not_able_to_change_order || draft_order === 1}
									data-order={parseInt(draft_order, 10) - 1}
									data-id={player.id}
									onClick={updatePlayerPreDraftListOrder}
								>
									<ArrowUp size='1.5' />
								</SwitchButton>
								<SwitchButton
									disabled={(
										is_not_able_to_change_order
										|| pre_draft_list_size === draft_order
									)}
									data-order={parseInt(draft_order, 10) + 1}
									data-id={player.id}
									onClick={updatePlayerPreDraftListOrder}
								>
									<ArrowDown size='1.5' />
								</SwitchButton>
							</div>
						</div>
					</TdYourTeam>
					<TdStat>
						<PlayerInfo className="player-info" player={player} />
					</TdStat>
					{this.props.table_keys.map(({ key, prop, prop_sub }) => {
						const renderValue = () => (
							<React.Fragment>
								{_.get(player, prop, '--')} {prop_sub
									? `(${_.get(player, prop_sub, '--')})`
									: null
								}
							</React.Fragment>
						);

						return (
							<TdStat className={`player-stats ${isActiveTab(prop)}`} key={key}>
								{is_coach_active && !has_assistant_coach ? (
									<CoachLockIcon />
								) : renderValue()}
							</TdStat>
						);
					})}
					<Td>
						{is_my_turn ? (
							<DraftButton
								disabled={is_draft_disabled}
								onClick={() => this.pickAPlayer(player)}
							/>
						) : (
							<Checkbox
								onChange={this.onClickIgnore}
								checked={is_ignored}
								name={`ignore-${player.id}`}
							/>
						)}
					</Td>
				</TrRow>
			);
		});
		const
			{ picks_by_player_id } = this.state,
			{ teams_by_id, table_keys } = this.props;

		const drafted_cells = by_drafted ? this.drafted_players.map(player => {
			const
				pick_object = _.get(picks_by_player_id, player.id, {}),
				user = _.get(teams_by_id, pick_object.picked_team_id, {}),
				is_compressed = false;

			return (
				<TrRowDrafted key={player.id}>
					<Td>
						<DragIconWrapper>
							<DragIcon
								onMouseEnter={onEnableDrag}
								onMouseLeave={onDisableDrag}
								disabled
							/>
						</DragIconWrapper>

					</Td>
					<TdDraftedPlayerInfo is_compressed={is_compressed}>
						<DraftedPlayerPickNum>
							{is_compressed ? 'Pick' : 'Drafted at pick'}
							{' '}
							{pick_object.pick - 1}
						</DraftedPlayerPickNum>
						<DraftedPlayerPickUserName>
							{user.firstname}
							{' '}
							{is_compressed ? `${_.get(user, 'lastname[0]')}.` : user.lastname}
						</DraftedPlayerPickUserName>
					</TdDraftedPlayerInfo>
					<TdStat>
						<PlayerInfo
							className="player-info"
							player={player}
							is_compressed={is_compressed}
						/>
					</TdStat>
					{table_keys.map(({ key, prop, prop_sub }) => {
						const renderValue = () => (
							<React.Fragment>
								{_.get(player, prop, '--')} {prop_sub
									? `(${_.get(player, prop_sub, '--')})`
									: null
								}
							</React.Fragment>
						);
						return (
							<TdStat className={`player-stats ${isActiveTab(prop)}`} key={key}>
								{renderValue()}
							</TdStat>
						);
					})}
					<Td>
						<NoDropWrapper>
							<NoDrop size={1} />
						</NoDropWrapper>
					</Td>
				</TrRowDrafted>
			);
		}) : null;

		if (!is_pending_players && _.isEmpty(players_list)) {
			return this.getTableMessage(
				'No Players within the player pool fit your search criteria'
			);
		}

		return (
			<TBody>
				{by_drafted && !_.isEmpty(drafted_cells) ? (
					<TrRow>
						<Td style={{ padding: 0 }} colSpan={this.columns_size}>
							<DraftedPlayersList>
								<TableDraftedFixed>
									{this.table_header}
									<TBody>
										{drafted_cells}
									</TBody>
								</TableDraftedFixed>
							</DraftedPlayersList>
						</Td>
					</TrRow>
				) : null}
				{cells}
			</TBody>
		);
	}

	pickAPlayer(player_for_pick: TPlayerWithFixture) {
		if (this.state.is_my_turn) {
			this.setState({ player_for_pick });
		}
	}

	onClickIgnore(event) {
		const { currentTarget: { name, checked } } = event;

		if (checked && !window.confirm('Are you sure you want to ignore this player?')) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		const { league: { team_id } } = this.props;
		const player_id = parseInt(_.last(name.split('-')), 10);

		const togglePlayerInList = this.props[
			!checked ? 'addPlayerToPreDraftList' : 'removePlayerFromPreDraftList'
		];

		togglePlayerInList({ player_id, team_id });
	}

	getAvailablePositions(player) {
		const { teams, league: { team_id } } = this.props;
		const { lineup } = _.get(teams, team_id, {});

		if (!lineup) {
			return [];
		}

		const position_keys = _.concat(_.get(player, 'positions', []), 'bench');
		const fullLine = _.flow([
			_.partial(_.filter, _, _.negate(_.identity)),
			_.isEmpty
		]);

		return _.chain(lineup).pick(position_keys).omitBy(fullLine).keys().value();
	}

	get pick_modal() {
		const { player_for_pick, is_my_turn, next_pick_at, last_pick } = this.state;
		return player_for_pick && is_my_turn && !this.isDrafted(player_for_pick) ? (
			<MakePickPopup
				availablePositions={this.getAvailablePositions(player_for_pick)}
				player={player_for_pick}
				pickMsg={last_pick}
				nextPickAt={next_pick_at}
				confirmClick={this.confirmPlayerPick}
				closeClick={this.closePickModal}
			/>
		) : null;
	}

	get error_modal() {
		const { team_error } = this.props;

		return (
			<ModalContainer header_text="Error" onClick={this.handleErrorModalClose}>
				<ModalInnerTitle>
					{team_error}
				</ModalInnerTitle>
			</ModalContainer>
		);
	}

	handleErrorModalClose() {
		const { clearTeamErrors } = this.props;
		clearTeamErrors();
	}

	closePickModal() {
		this.setState({ player_for_pick: undefined });
	}

	confirmPlayerPick(event: Object) {
		const { league, postPickedPlayer } = this.props;
		const { id, pos } = event.currentTarget.dataset;

		postPickedPlayer({
			team_id: league.team_id,
			player_id: id,
			position: pos,
		});

		this.closePickModal();
	}

	setPlayerPopup(player: Object) {
		this.setState({ player_drafted_popup: player });
	}

	closePlayerPopup() {
		this.setState({
			player_drafted_popup: undefined
		});
	}

	onToggleAutoPick(event: Object) {
		const is_enabled = event.currentTarget.checked;
		const status = is_enabled ? 'on' : 'off';
		const { league: { team_id }, postAutoPickFlag } = this.props;

		this.setState({
			is_auto_pick_enabled: event.currentTarget.checked
		});

		postAutoPickFlag({ team_id, status });
	}

	// eslint-disable-next-line complexity
	render() {
		const {
			squads,
			positions,
			playerStatuses,
			league: { draft_status, name },
			league,
			match: { params: { league_id } },
			coach_stats_list,
			order_by_desc,
			onToggleOrder,
			onFiltersChange,
			onFilterByPosition,
			onTabChange,
			onSearchSubmit,
			onSelectChange,
			is_coach_active,
			user: { team_name },
			users_by_id,
			filters,
		} = this.props;

		const {
			live_draft_complete,
			is_my_turn,
			next_pick_at,
			last_pick,
			picked_players_by_pick,
			player_drafted_popup
		} = this.state;


		const picksPerTeam = getSquadSize(league);
		const numPicks = picksPerTeam * league.size;
		const lastPickPlayer = _.get(last_pick, 'picked_player_id');
		const lastPickIsFinal = _.get(last_pick, 'pick') === numPicks;
		const isDraftDone = _.every(
			[lastPickIsFinal,
				_.isNumber(lastPickPlayer)]
		);


		if (draft_status === 'scheduled') {
			return <Redirect to={`/league/${league_id}/team/pre-draft`} />;
		}

		if (draft_status === 'complete' || (live_draft_complete && isDraftDone)) {
			return <Redirect to={`/league/${league_id}/team`} />;
		}
		// F2P1-62534: Display even in mobile
		// if (isMobile()){
		// 	return <GoToApp 
		// 		page_name='Live Draft'
		// 	/>;
		// }

		return (
			<React.Fragment>
				<DraftBarContainer>
					<DraftTurnTimer
						nextPickAt={next_pick_at}
						isMyTurn={is_my_turn}
						lastPick={last_pick}
						user={_.get(users_by_id, _.get(last_pick, 'turn_user_id'), '')}
					/>
					<DraftTeamListOrderIndicator
						currentPick={last_pick}
						isMyTurn={is_my_turn}
						league={league}
					/>
					<AutoPickToggle
						isAutoPickEnabled={this.is_auto_pick_enabled}
						onChange={this.onToggleAutoPick}
					/>
				</DraftBarContainer>
				<DraftLayout sidebar_open={this.is_sidebar_open}>
					<div>
						<DraftTitleContainer>
							<PageTitle>Live Draft for {name}</PageTitle>
						</DraftTitleContainer>
						<DraftFilters
							handleChange={onFiltersChange}
							handleSubmit={onSearchSubmit}
							onToggleOrder={onToggleOrder}
							order_by_desc={order_by_desc}
							squads={squads}
							positions={positions}
							statuses={playerStatuses}
							is_show_drafted={true}
							is_show_ignored={true}
							selected_by_position={filters.by_position}
						/>
						<DraftTableHeader
							onTabClick={onTabChange}
							onSelectChange={onSelectChange}
							isCoachActive={is_coach_active}
							selectOptions={coach_stats_list}
						>
						</DraftTableHeader>
						<TableFixed>
							{this.table_header}
							{this.table_body}
						</TableFixed>
						{this.load_more_btn}
					</div>
					<Aside>
						<TeamViewWidget
							title={team_name}
							setPlayer={this.setPlayerPopup}
							onToggle={this.onToggleTeamViewWidget}
							onFilterByPosition={onFilterByPosition}
							widget_open={this.state.team_view_widget_open}
							league={league}
						>
						</TeamViewWidget>
						<DraftOrderWidget
							onToggle={this.onToggleDraftOrderWidget}
							widget_open={this.state.draft_order_widget_open}
							is_live
							picked_players_by_pick={picked_players_by_pick}
							league={league}
						/>
					</Aside>
				</DraftLayout>
				{this.pick_modal}
				{player_drafted_popup ? (
					<PlayerProfilePopup
						player={player_drafted_popup}
						closeClick={this.closePlayerPopup}
					/>
				) : null}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: TRootStore, props) => ({
	teams: selectors.complex.team.getTeamsWithFullLineup(state, props),
	teams_by_id: selectors.leagueDraft.getTeamsById(state),
	users_by_id: selectors.getUsersFromTeamsById(state),
	team_error: state.teamsDraft.error,
});

const mapDispatchToProps = {
	showDraftOrder: actions.leagueDraft.showDraftOrder,
	postAutoPickFlag: actions.teamsDraft.postAutoPickFlag,
	postPickedPlayer: actions.teamsDraft.postPickedPlayer,
	clearTeamErrors: actions.teamsDraft.clearTeamErrors,
};

export const LiveDraft = connect(
	mapStateToProps,
	mapDispatchToProps,
)(withRouter(withDraftCore(LiveDraftContainer)));

export default LiveDraft;


// WEBPACK FOOTER //
// ./src/pages/league/LiveDraft.js